import { useMemo } from "react";
import { STUDENT_LIST } from "@/api/queries/studentList";
import { StudentListQuery } from "@/__generated__/graphql";
import { ApolloError, useQuery } from "@apollo/client";
import { useOrg } from "@/hooks/useOrg";
import { processStudent } from "@/util/studentUtils";
import { Student } from "@/types/Student";
import { useOrgPermissions } from "./useOrgPermissions";
import {
  PartnerFeatureEnum,
  YesNoEnum,
  StudentListArgumentInput,
} from "@/__generated__/types";
import { CURRENT_USER } from "@/api/queries/currentUser";

export interface StudentSearchResults {
  students: Student[];
  loading: boolean;
  error: ApolloError | undefined;
}

export function useStudentSearch({
  search,
}: {
  search?: string;
}): StudentSearchResults {
  const { org } = useOrg();
  const { data: userData } = useQuery(CURRENT_USER);
  const {
    me: { id: userId },
  } = userData || { me: {} };

  const { hasOrgFeature } = useOrgPermissions();
  const hasRise = hasOrgFeature(PartnerFeatureEnum.Rise);

  const argument: StudentListArgumentInput = useMemo(() => {
    return {
      search: {
        inCommunity: !hasRise ? YesNoEnum.Yes : undefined,
        searchingUserId: userId ?? "",
        name: search ?? "",
      },
    };
  }, [hasRise, search, userId]);

  const { data, loading, error } = useQuery<StudentListQuery>(STUDENT_LIST, {
    variables: {
      orgId: org?.id,
      argument: argument,
    },
    skip: !org?.id || !search || !argument || !userId,
  });

  return useMemo(() => {
    const studentsResult = {
      students: [],
      loading,
      error,
    };

    const node = data?.node;
    if (node?.__typename === "Organization") {
      const students =
        node.studentList.edges
          ?.map((e) => {
            return e?.node ? processStudent(e?.node) : false;
          })
          .filter((student) => !!student) || [];

      return {
        ...studentsResult,
        students,
      };
    }

    return studentsResult;
  }, [data, error, loading]);
}
